import React, { useState, useEffect } from 'react'

interface IndicadorModalProps {
  show: boolean
  editing: boolean
  formData: any
  indicadores: any[]
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
  onChange: (data: any) => void
}

export default function IndicadorModal({ show, editing, formData, indicadores, onClose, onSubmit, onChange }: IndicadorModalProps) {
  if (!show) return null

  const indicadoresLancamento = indicadores.filter(ind => 
    ind.tipo_indicador === 'lancamento' || !ind.tipo_indicador
  )

  const handleTipoIndicadorChange = (tipo: string) => {
    onChange({
      ...formData,
      tipo_indicador: tipo,
      formula_calculo: tipo === 'calculado' ? 'PERCENTUAL_CONVERSAO' : null,
      indicadores_dependentes: tipo === 'calculado' ? {} : null
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">{editing ? 'Editar' : 'Novo'} Indicador</h2>
        
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Tipo de Indicador */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <label className="block text-sm font-semibold mb-3 text-blue-900">Tipo de Indicador</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleTipoIndicadorChange('lancamento')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.tipo_indicador === 'lancamento' || !formData.tipo_indicador
                    ? 'border-blue-500 bg-blue-100 text-blue-900'
                    : 'border-gray-300 bg-white hover:border-blue-300'
                }`}
              >
                <div className="text-2xl mb-2">✍️</div>
                <div className="font-bold">Lançamento Manual</div>
                <div className="text-xs mt-1">Usuário insere valores</div>
              </button>
              
              <button
                type="button"
                onClick={() => handleTipoIndicadorChange('calculado')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.tipo_indicador === 'calculado'
                    ? 'border-green-500 bg-green-100 text-green-900'
                    : 'border-gray-300 bg-white hover:border-green-300'
                }`}
              >
                <div className="text-2xl mb-2">🤖</div>
                <div className="font-bold">Calculado Automático</div>
                <div className="text-xs mt-1">Sistema calcula</div>
              </button>
            </div>
          </div>

          {/* Nome */}
          <div>
            <label className="block text-sm font-medium mb-2">Nome do Indicador *</label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => onChange({...formData, nome: e.target.value})}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="Ex: Total de Leads Capturados"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium mb-2">Tipo de Valor</label>
              <select
                value={formData.tipo}
                onChange={(e) => onChange({...formData, tipo: e.target.value})}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="quantidade">Quantidade</option>
                <option value="percentual">Percentual</option>
                <option value="monetario">Monetário</option>
                <option value="taxa">Taxa</option>
              </select>
            </div>

            {/* Categoria */}
            <div>
              <label className="block text-sm font-medium mb-2">Categoria</label>
              <select
                value={formData.categoria}
                onChange={(e) => onChange({...formData, categoria: e.target.value})}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="funil">Funil</option>
                <option value="financeiro">Financeiro</option>
                <option value="conversao">Conversão</option>
                <option value="custo">Custo</option>
                <option value="performance">Performance</option>
              </select>
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium mb-2">Descrição</label>
            <textarea
              value={formData.descricao}
              onChange={(e) => onChange({...formData, descricao: e.target.value})}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              rows={3}
              placeholder="Descreva o que este indicador mede"
            />
          </div>

          {/* Campos específicos para indicadores calculados */}
          {formData.tipo_indicador === 'calculado' && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 space-y-4">
              <h3 className="font-bold text-green-900 flex items-center gap-2">
                <span>🤖</span>
                Configuração de Cálculo Automático
              </h3>

              {/* Fórmula */}
              <div>
                <label className="block text-sm font-medium mb-2">Fórmula de Cálculo *</label>
                <select
                  value={formData.formula_calculo || ''}
                  onChange={(e) => onChange({...formData, formula_calculo: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                  required={formData.tipo_indicador === 'calculado'}
                >
                  <option value="">Selecione uma fórmula</option>
                  <option value="PERCENTUAL_CONVERSAO">Percentual de Conversão (A / B × 100)</option>
                  <option value="CUSTO_MEDIO">Custo Médio (Investimento / Quantidade)</option>
                  <option value="ROI">ROI - Retorno sobre Investimento</option>
                  <option value="CAC">CAC - Custo de Aquisição de Cliente</option>
                  <option value="TICKET_MEDIO">Ticket Médio (Faturamento / Vendas)</option>
                  <option value="PERCENTUAL_LUCRO">Percentual de Lucro</option>
                </select>
              </div>

              {/* Indicadores Dependentes */}
              <div>
                <label className="block text-sm font-medium mb-2">Indicadores Necessários para o Cálculo</label>
                <p className="text-xs text-gray-600 mb-3">
                  Selecione os indicadores de lançamento que serão usados no cálculo
                </p>

                {formData.formula_calculo === 'PERCENTUAL_CONVERSAO' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Numerador (A)</label>
                      <select
                        value={formData.indicadores_dependentes?.numerador || ''}
                        onChange={(e) => onChange({
                          ...formData,
                          indicadores_dependentes: {
                            ...formData.indicadores_dependentes,
                            numerador: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        required
                      >
                        <option value="">Selecione</option>
                        {indicadoresLancamento.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Denominador (B)</label>
                      <select
                        value={formData.indicadores_dependentes?.denominador || ''}
                        onChange={(e) => onChange({
                          ...formData,
                          indicadores_dependentes: {
                            ...formData.indicadores_dependentes,
                            denominador: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        required
                      >
                        <option value="">Selecione</option>
                        {indicadoresLancamento.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {(formData.formula_calculo === 'CUSTO_MEDIO' || formData.formula_calculo === 'CAC') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Investimento Total</label>
                      <select
                        value={formData.indicadores_dependentes?.investimento || ''}
                        onChange={(e) => onChange({
                          ...formData,
                          indicadores_dependentes: {
                            ...formData.indicadores_dependentes,
                            investimento: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        required
                      >
                        <option value="">Selecione</option>
                        {indicadoresLancamento.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        {formData.formula_calculo === 'CAC' ? 'Total de Vendas' : 'Quantidade'}
                      </label>
                      <select
                        value={formData.indicadores_dependentes?.quantidade || ''}
                        onChange={(e) => onChange({
                          ...formData,
                          indicadores_dependentes: {
                            ...formData.indicadores_dependentes,
                            quantidade: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        required
                      >
                        <option value="">Selecione</option>
                        {indicadoresLancamento.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {(formData.formula_calculo === 'ROI' || formData.formula_calculo === 'TICKET_MEDIO' || formData.formula_calculo === 'PERCENTUAL_LUCRO') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Faturamento Total</label>
                      <select
                        value={formData.indicadores_dependentes?.faturamento || ''}
                        onChange={(e) => onChange({
                          ...formData,
                          indicadores_dependentes: {
                            ...formData.indicadores_dependentes,
                            faturamento: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        required
                      >
                        <option value="">Selecione</option>
                        {indicadoresLancamento.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        {formData.formula_calculo === 'TICKET_MEDIO' ? 'Total de Vendas' : 'Investimento Total'}
                      </label>
                      <select
                        value={formData.indicadores_dependentes?.[formData.formula_calculo === 'TICKET_MEDIO' ? 'vendas' : 'investimento'] || ''}
                        onChange={(e) => onChange({
                          ...formData,
                          indicadores_dependentes: {
                            ...formData.indicadores_dependentes,
                            [formData.formula_calculo === 'TICKET_MEDIO' ? 'vendas' : 'investimento']: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        required
                      >
                        <option value="">Selecione</option>
                        {indicadoresLancamento.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ativo */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.ativo !== false}
              onChange={(e) => onChange({...formData, ativo: e.target.checked})}
              className="mr-2 w-4 h-4"
            />
            <label className="text-sm font-medium">Indicador Ativo</label>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 font-semibold shadow-lg"
            >
              {editing ? 'Atualizar' : 'Criar'} Indicador
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
